import { Choice, ChoiceList, IconDecoration } from '@/components/ChoiceList'
import { Icon } from '@/components/Icon'
import { useCallback } from 'react'
import type { Selection } from 'react-aria-components'

interface BoolQuestionAnswerEditorContainerProps {
  value: boolean | null
  onChange: (value: boolean) => unknown
  isDisabled?: boolean
  isInvalid?: boolean
  'aria-labelledby'?: string
  'aria-describedby'?: string
  testId?: string
  yesTestId?: string
  noTestId?: string
}

/**
 * Boolean question answer editor.
 */
export function BoolQuestionAnswerEditor({
  value,
  onChange,
  isDisabled,
  testId,
  yesTestId,
  noTestId,
  ...rest
}: BoolQuestionAnswerEditorContainerProps) {
  const onSelectionChange = useCallback(
    (selection: Selection) => {
      if (!(selection instanceof Set)) {
        return
      }

      if (selection.size === 0) {
        return
      }

      const newValue = selection.has('yes')
      onChange(newValue)
    },
    [onChange],
  )

  return (
    <ChoiceList
      mode="single"
      layout="grid"
      items={items}
      disabledKeys={isDisabled ? allIds : undefined}
      disallowEmptySelection
      selectedKeys={
        value === null ? selectionEmpty : value ? selectionYes : selectionNo
      }
      onSelectionChange={onSelectionChange}
      testId={testId}
      {...rest}
    >
      {(item) => (
        <Choice
          withoutSelectionIcon
          id={item.id}
          label={item.label}
          testId={item.id === 'yes' ? yesTestId : noTestId}
          decoration={
            <IconDecoration>
              <Icon name={item.id === 'yes' ? 'tick' : 'close'} />
            </IconDecoration>
          }
        />
      )}
    </ChoiceList>
  )
}

const items = [box(true), box(false)]
const allIds = items.map((x) => x.id)
const selectionEmpty = new Set<string>([])
const selectionYes = new Set<string>(['yes'])
const selectionNo = new Set<string>(['no'])

function box(value: boolean) {
  return {
    label: value ? 'Yes' : 'No',
    id: value ? 'yes' : 'no',
    value,
  } as const
}
